import axios, { AxiosInstance } from 'axios';
import { API_ENDPOINTS } from '../shared/constants';

// AI Service Types
export interface ThreatAnalysisRequest {
  text: string;
  location?: string;
  timestamp?: string;
  user_id?: string;
  context?: Record<string, any>;
}

export interface ThreatAnalysisResult {
  threat_score: number;
  threat_level: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  analysis_details: Record<string, any>;
  timestamp: string;
}

export interface BehaviorAnalysisRequest {
  user_id: string;
  actions: Array<{
    description: string;
    duration: number;
    frequency: number;
    type: string;
  }>;
  timeframe?: string;
  context?: Record<string, any>;
}

export interface BehaviorAnalysisResult {
  anomaly_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  patterns_detected: string[];
  recommendations: string[];
  timestamp: string;
}

export interface AIHealthStatus {
  status: string;
  timestamp: string;
  version: string;
  ai_models: boolean;
}

class AIService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // AI Service URL - Railway deployed
    this.baseURL = 'https://getmyhive-ai-v2-production.up.railway.app';
    console.log('🧠 AI Service URL:', this.baseURL);
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds for AI processing
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('🚨 AI Service Error:', error);
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * 🚨 Analyze threat using AI
   */
  async analyzeThreat(request: ThreatAnalysisRequest): Promise<ThreatAnalysisResult> {
    try {
      console.log('🔍 Analyzing threat:', request.text);
      
      const response = await this.api.post(API_ENDPOINTS.AI.THREAT_ANALYSIS, request);
      
      console.log('✅ Threat analysis completed:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Threat analysis failed:', error);
      throw error;
    }
  }

  /**
   * 👤 Analyze user behavior using AI
   */
  async analyzeBehavior(request: BehaviorAnalysisRequest): Promise<BehaviorAnalysisResult> {
    try {
      console.log('🔍 Analyzing behavior for user:', request.user_id);
      
      const response = await this.api.post(API_ENDPOINTS.AI.BEHAVIOR_ANALYSIS, request);
      
      console.log('✅ Behavior analysis completed:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Behavior analysis failed:', error);
      throw error;
    }
  }

  /**
   * 🩺 Check AI service health
   */
  async getHealth(): Promise<AIHealthStatus> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AI.HEALTH);
      return response.data;
    } catch (error: any) {
      console.error('❌ AI health check failed:', error);
      throw error;
    }
  }

  /**
   * 📊 Get AI service metrics
   */
  async getMetrics(): Promise<string> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AI.METRICS);
      return response.data;
    } catch (error: any) {
      console.error('❌ AI metrics failed:', error);
      throw error;
    }
  }

  /**
   * 🚀 Test AI service connectivity
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.api.get('/');
      return response.data?.status === 'operational';
    } catch (error) {
      console.error('❌ AI service connection test failed:', error);
      return false;
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (error.response?.data?.detail) {
      return new Error(error.response.data.detail);
    }
    
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    
    if (error.message) {
      return new Error(error.message);
    }
    
    return new Error('AI Service bağlantı hatası');
  }

  /**
   * Get threat level color for UI
   */
  getThreatLevelColor(level: string): string {
    switch (level) {
      case 'critical': return '#DC2626'; // red-600
      case 'high': return '#EA580C';     // orange-600  
      case 'medium': return '#D97706';   // amber-600
      case 'low': return '#16A34A';      // green-600
      default: return '#6B7280';         // gray-500
    }
  }

  /**
   * Get threat level icon for UI
   */
  getThreatLevelIcon(level: string): string {
    switch (level) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '⚡';
      case 'low': return '✅';
      default: return '❓';
    }
  }

  // Training Management
  async getTrainingModels(): Promise<any> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AI.TRAINING_MODELS);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get training models:', error);
      throw error;
    }
  }

  async getTrainingStatus(): Promise<any> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AI.TRAINING_STATUS);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get training status:', error);
      throw error;
    }
  }

  async submitFeedback(feedback: {
    feedback_type: string;
    user_id: string;
    original_prediction: any;
    correct_response: any;
    feedback_text?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post(API_ENDPOINTS.AI.TRAINING_FEEDBACK, feedback);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit feedback:', error);
      throw error;
    }
  }

  async getLearningSource(): Promise<any> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AI.LEARNING_SOURCES);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get learning sources:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const aiService = new AIService();
export default aiService;
